/* eslint-disable react/prop-types */
/* eslint-disable react-hooks/exhaustive-deps */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useRouter } from 'next/router';
import VideoFrame from '@cont/ClassPage/VideoFrame';
import pushRoute from '@utils/pushRoute';
import { useSelector, useDispatch } from 'react-redux';

import {
  closePlayer,
  setPlayerLoaded,
  resetPlayerLoaded,
  setShowReferral
} from '@actions/player';
import {
  sendWatched,
  sendWatchedPrevious,
  submitLiveClassReview
} from '@actions/classes';
import Item from '@models/Item';
import User from '@models/User';
import Course from '@models/Course';
import Cookies from '@utils/cookies';
import SegmentEventsUtil from '@utils/SegmentEventsUtils';
import { setDarkMode } from '@actions/auth';

const initialClassDetails = {
  streamUrl: 'about:blank',
  selectedLessonData: {}
};

const VideoModal = ({ noDisplay, toggleDarkMode }) => {
  const dispatch = useDispatch();
  const [classDetails, setClassDetails] = useState(initialClassDetails);
  const [reloadState, forceUpdate] = useState();
  const [sendSubCardEvents, setSendSubCardEvents] = useState({
    subscriptionCardVisibleOnIframe: false,
    getSubscriptionClickedOnIframe: false
  });
  const router = useRouter();
  const classUID = useRef();

  const { route, query } = router;
  const isExternal = query?.external === '1';

  const isLoggedIn = useSelector(({ auth }) => auth.isLoggedIn);
  const me = useSelector(({ auth }) => auth.me);
  const accessToken = useSelector(({ auth }) => auth.accessToken);
  const unLiveURL = useSelector(({ player }) => player.unLiveURL);
  const urlMismatch = useSelector(({ player }) => player.urlMismatch);
  const isPlayerOpen = useSelector(({ player }) => player.isPlayerOpen);
  const playerLoaded = useSelector(({ player }) => player.playerLoaded);
  const classData = useSelector(({ player }) => player.classData);

  const unloadIframe = () => {
    dispatch(resetPlayerLoaded());
    dispatch(closePlayer());
  };

  const backButtonHandler = useCallback((event) => {
    dispatch(setShowReferral(classUID));
    if (event?.state?.key) {
      unloadIframe();
      return;
    }

    const iframeRef = document.getElementById('nativeIframeOne')?.contentWindow;
    if (iframeRef) {
      iframeRef.postMessage(
        {
          iframeClosed: true
        },
        '*'
      );
    }

    unloadIframe();
    if (route.includes('classroom')) {
      pushRoute('/', '/');
    } else if (event.buttonPressed) {
      window.history.back();
    }
  }, []);

  const onMessageReceivedHandler = useCallback(
    (e) => {
      if (e.data.subscriptionCardVisibleOnIframe) {
        setSendSubCardEvents({
          ...sendSubCardEvents,
          subscriptionCardVisibleOnIframe: true
        });
      }
      if (e.data.getSubscriptionClickedOnIframe) {
        setSendSubCardEvents({
          ...sendSubCardEvents,
          getSubscriptionClickedOnIframe: true
        });
      }
      if (e.data.liveClassReady) {
        dispatch(setPlayerLoaded());
      }
      if (e.data.isFeedbackData) {
        dispatch(submitLiveClassReview(e.data.feedbackData));
      }
      if (e.data.closeIframe) {
        backButtonHandler({ buttonPressed: true });
      }
      if (e.data.isNextLessonClicked) {
        const updatedData = {
          ...e.data.nextLesson,
          type: 'post'
        };
        const { as } = Item.getItemRedirectURL(updatedData, true) || {};
        window.open(as, '_self');
      }
      if (e.data.reloadIframe) {
        dispatch(resetPlayerLoaded());
        forceUpdate({});
      }
      if (e.data.setDarkModeOn) {
        Cookies.setCookie(
          'isDarkModeOn',
          e.data.setDarkModeOn.shouldSet ? 'true' : ''
        );
        dispatch(setDarkMode(e.data.setDarkModeOn.shouldSet));
        toggleDarkMode(e.data.setDarkModeOn.shouldSet);
      }
      if (e.data.segmentEvents) {
        const segmentEventsUtil = new SegmentEventsUtil(e.data.eventType.name, {
          ...e.data.eventType.data,
          is_enrolled: classData.data?.programme?.is_enrolled,
          is_special: classData.data?.programme?.is_special,
          is_saved: classData.data?.is_saved,
          programme_type: classData.data?.programme?.programme_type
        });
        segmentEventsUtil.sendEvents();
      }
    },
    [classData.loaded]
  );

  useEffect(() => {
    return () => {
      unloadIframe();
      window.removeEventListener('message', onMessageReceivedHandler);
    };
  }, []);

  const iframeOnOpenSetup = () => {
    window.addEventListener('popstate', backButtonHandler, false);
    document.body.style.overflow = 'hidden';
    const divToBeAnimated = document.getElementById('nativeIframeWrapper');
    if (divToBeAnimated) {
      setTimeout(() => divToBeAnimated.classList.add('show-loader'), 100);
    }
  };

  const iframeOnCloseCleanup = () => {
    dispatch({
      type: 'CLASS_MODAL_CLOSE'
    });
    window.removeEventListener('popstate', backButtonHandler);
    const divToBeAnimated = document.getElementById('nativeIframeWrapper');
    if (divToBeAnimated) {
      divToBeAnimated.classList.remove('show-loader');
    }
    document.body.style.overflow = 'auto';
  };

  const getClassUrlAndData = () => {
    let selectedLessonData = {};
    let streamUrl = unLiveURL;
    if (classData.data) {
      selectedLessonData = Item.getEntityData(classData.data);
      selectedLessonData.goalUid =
        classData.data.programme.goalUid || classData.data.programme.goal.uid;
      selectedLessonData.programme = Course.getEntityData(
        classData.data.programme
      );
      selectedLessonData.course = {
        title: classData.data?.programme?.name
      };
      selectedLessonData.author = User.getEntityData(classData.data.author);
      if (urlMismatch) {
        streamUrl = Item.getVideoUrl('', selectedLessonData, isExternal);
      } else {
        streamUrl = Item.getVideoUrl(unLiveURL, selectedLessonData, isExternal);
      }
    }

    // eslint-disable-next-line prefer-destructuring
    if (streamUrl) streamUrl = streamUrl.split('?')[0];

    return {
      streamUrl,
      selectedLessonData
    };
  };

  const loadIframe = () => {
    if (classData.loaded && classData.data) {
      const details = getClassUrlAndData();
      if (details.streamUrl === classDetails.streamUrl) {
        /**
         * If streamUrl in the state is same as the class streamUrl,
         * no need to reload the iframe. Just open it.
         */
        dispatch(setPlayerLoaded());
      }
      setClassDetails(getClassUrlAndData());
    }
  };

  useEffect(() => {
    classUID.current = classData?.data?.uid;
    if (isPlayerOpen) {
      window.addEventListener('message', onMessageReceivedHandler);
      loadIframe();
    } else {
      window.removeEventListener('message', onMessageReceivedHandler);
    }
  }, [isPlayerOpen]);

  useEffect(() => {
    if (isPlayerOpen && playerLoaded) {
      iframeOnOpenSetup();
    } else {
      iframeOnCloseCleanup();
    }
  }, [playerLoaded]);

  /**
   * Updated code ends
   */

  return !noDisplay ? (
    <VideoFrame
      isLoggedIn={isLoggedIn}
      hasAccess={playerLoaded}
      streamUrl={classDetails.streamUrl}
      isFullScreen
      classDetails={classDetails.selectedLessonData}
      loggedInUser={me}
      accessToken={accessToken}
      sendWatched={sendWatched}
      sendWatchedPrevious={sendWatchedPrevious}
      showPlayer={playerLoaded}
      iframeAlwaysPresent
      backButtonHandler={backButtonHandler}
      urlMismatch={urlMismatch}
      reloadState={reloadState}
      onMessageReceivedHandler={onMessageReceivedHandler}
      sendSubCardEvents={sendSubCardEvents}
      setSendSubCardEvents={setSendSubCardEvents}
    />
  ) : null;
};

export default VideoModal;
